/**
 * Product Page Interactions
 * - Thumbnail → main image slider
 * - Variant option buttons → variant selection + price update
 * - Quantity +/- on product form
 */

class ProductPage {
  constructor() {
    this.root = document.querySelector('.product-page');
    if (!this.root) return;
    this.form = this.root.querySelector('form[action*="/cart/add"]') || this.root.querySelector('form[action="/cart/add"]');
    this.gallery = this.root.querySelector('[data-gallery]');
    this.track = this.root.querySelector('[data-track]');
    this.slides = Array.from(this.root.querySelectorAll('.product-slide'));
    this.currentIndex = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.activePointers = new Map();
    this.baseScale = 1;
    this.scale = 1;
    this.isPinching = false;
    this.skeleton = this.root.querySelector('[data-skeleton]');
    this.thumbs = Array.from(this.root.querySelectorAll('.product-page__thumb'));
    this.optionButtons = Array.from(this.root.querySelectorAll('.product-page__option-btn'));
    this.quantityInput = this.root.querySelector('.product-page__quantity-input input[name="quantity"]');
    this.qtyMinus = this.root.querySelector('.product-page__quantity-input [data-quantity-minus]');
    this.qtyPlus = this.root.querySelector('.product-page__quantity-input [data-quantity-plus]');
    this.priceEl = this.root.querySelector('.product-page__price');
    this.submitBtn = this.root.querySelector('.product-page__add-btn');
    this.productData = this.loadProductData();
    this.selectedOptions = this.getInitialSelectedOptions();
    this.init();
  }

  loadProductData() {
    try {
      const script = this.root.querySelector('[id^="ProductData-"]');
      if (!script) return null;
      return JSON.parse(script.textContent);
    } catch (_) {
      return null;
    }
  }

  getInitialSelectedOptions() {
    const selected = {};
    this.optionButtons
      .filter(btn => btn.classList.contains('active'))
      .forEach(btn => {
        const name = btn.dataset.optionName;
        const value = btn.dataset.optionValue;
        if (name && value) selected[name] = value;
      });
    return selected;
  }

  init() {
    this.initGallery();
    this.initThumbs();
    this.initOptions();
    this.initQuantity();
    this.initAddToCart();
  }

  initAddToCart() {
    if (!this.form || !this.submitBtn) return;
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      this.setSubmitState(true);
      try {
        const formData = new FormData(this.form);
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Add to cart failed');
        const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
        this.updateHeaderCart(cart.item_count);
        // Trigger WOW Cart Plugin to open
        this.openCartDrawer();
      } catch (err) {
        console.error('Cart error:', err);
      } finally {
        this.setSubmitState(false);
      }
    });
  }

  setSubmitState(loading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = loading;
    const span = this.submitBtn.querySelector('span');
    if (span) {
      if (loading) {
        this.submitBtn.dataset.originalText = span.textContent;
        span.textContent = 'adding...';
      } else {
        span.textContent = this.submitBtn.dataset.originalText || 'add to cart';
      }
    }
  }

  updateHeaderCart(count) {
    const countEl = document.querySelector('[data-cart-count]');
    if (countEl) {
      countEl.textContent = count;
      countEl.classList.toggle('hidden', count === 0);
    }
  }

  openCartDrawer() {
    // Notify any cart-drawer integrations and tracking scripts that the cart just changed.
    // (This previously also synthetically clicked .header__cart to trigger a third-party
    // drawer plugin's click listener. That plugin is no longer active, and .header__cart
    // is a plain <a href="/cart">, so the synthetic click just followed the link and
    // force-navigated to /cart on every add-to-cart — an unintended, race-prone side
    // effect that could also cut off other scripts listening for these events, such as
    // pixel tracking, before they finished running. Removed; events below are sufficient.)
    document.dispatchEvent(new CustomEvent('wow-cart-open'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  initGallery() {
    if (!this.gallery || !this.track || !this.slides.length) return;
    this.currentIndex = Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active')));
    this.slides.forEach((slide, idx) => {
      const img = slide.querySelector('img');
      if (img) {
        img.decoding = 'async';
        img.sizes = '(min-width: 1024px) 50vw, 100vw';
        img.addEventListener('load', () => this.hideSkeleton(), { once: true });
        img.addEventListener('error', () => {
          img.src = this.placeholderDataURI();
          this.hideSkeleton();
        }, { once: true });
        if (idx === this.currentIndex) {
          if (img.complete) this.hideSkeleton(); else this.showSkeleton();
        }
      }
    });
    this.setupVideoControls();
    this.updateTrack(false);
    this.preloadAdjacent();
    this.autoplayMediaAt(this.currentIndex);
    this.gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.goTo(this.currentIndex + 1);
      if (e.key === 'ArrowLeft') this.goTo(this.currentIndex - 1);
    });
    this.gallery.addEventListener('pointerdown', this.onPointerDown.bind(this));
    this.gallery.addEventListener('pointermove', this.onPointerMove.bind(this));
    this.gallery.addEventListener('pointerup', this.onPointerUp.bind(this));
    this.gallery.addEventListener('pointercancel', this.onPointerUp.bind(this));
    window.addEventListener('resize', () => this.updateTrack(false));
  }

  initThumbs() {
    if (!this.thumbs.length) return;
    this.thumbs.forEach(btn => {
      btn.addEventListener('click', () => {
        this.thumbs.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const idx = parseInt(btn.getAttribute('data-index') || '0', 10);
        this.goTo(idx);
      });
    });
  }

  goTo(index) {
    if (!this.slides.length) return;
    const total = this.slides.length;
    const target = ((index % total) + total) % total;
    if (target === this.currentIndex) return;
    this.pauseMediaAt(this.currentIndex);
    this.showSkeleton();
    this.slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === target);
      s.setAttribute('aria-hidden', i === target ? 'false' : 'true');
    });
    this.currentIndex = target;
    this.updateTrack(true);
    this.updateThumbs();
    const img = this.slides[this.currentIndex].querySelector('img');
    if (img) {
      if (img.complete) this.hideSkeleton();
      else {
        img.addEventListener('load', () => this.hideSkeleton(), { once: true });
        img.addEventListener('error', () => { img.src = this.placeholderDataURI(); this.hideSkeleton(); }, { once: true });
      }
    }
    // Autoplay videos on the newly selected slide
    this.autoplayMediaAt(this.currentIndex);
    this.preloadAdjacent();
  }

  setupVideoControls() {
    this.slides.forEach(slide => {
      const video = slide.querySelector('video.product-video');
      const overlay = slide.querySelector('[data-video-play]');
      const muteBtn = slide.querySelector('[data-video-mute]');
      const iframe = slide.querySelector('iframe.product-video-embed');
      if (video && overlay) {
        video.loop = true;
        overlay.addEventListener('click', () => {
          if (video.paused) {
            video.play().catch(() => {});
            overlay.classList.add('is-hidden');
          } else {
            video.pause();
            overlay.classList.remove('is-hidden');
          }
        });
        if (muteBtn) {
          muteBtn.addEventListener('click', () => {
            video.muted = !video.muted;
            muteBtn.querySelector('.mute-icon').textContent = video.muted ? '🔇' : '🔊';
          });
        }
        video.addEventListener('pause', () => {
          if (slide !== this.slides[this.currentIndex]) return;
          overlay.classList.remove('is-hidden');
        });
        video.addEventListener('play', () => {
          overlay.classList.add('is-hidden');
        });
      }
      if (iframe) {
        // Store original src for reset
        iframe.dataset.origSrc = iframe.src || '';
        const embedOverlay = slide.querySelector('[data-embed-play]');
        if (embedOverlay) {
          embedOverlay.addEventListener('click', () => {
            const base = iframe.dataset.origSrc || iframe.src || '';
            iframe.src = this.buildLoopingAutoplaySrc(base);
            embedOverlay.classList.add('is-hidden');
          });
        }
      }
    });
  }

  pauseMediaAt(i) {
    const slide = this.slides[i];
    if (!slide) return;
    const video = slide.querySelector('video.product-video');
    if (video) {
      video.pause();
      const overlay = slide.querySelector('[data-video-play]');
      if (overlay) overlay.classList.remove('is-hidden');
    }
    const iframe = slide.querySelector('iframe.product-video-embed');
    if (iframe && iframe.dataset.origSrc) {
      iframe.src = iframe.dataset.origSrc;
      const overlay = slide.querySelector('[data-embed-play]');
      if (overlay) overlay.classList.remove('is-hidden');
    }
  }

  autoplayMediaAt(i) {
    const slide = this.slides[i];
    if (!slide) return;
    // HTML5 video
    const video = slide.querySelector('video.product-video');
    const overlay = slide.querySelector('[data-video-play]');
    if (video) {
      video.muted = true;
      video.playsInline = true;
      video.loop = true;
      const onCanPlay = () => { this.hideSkeleton(); video.removeEventListener('playing', onCanPlay); };
      video.addEventListener('playing', onCanPlay, { once: true });
      video.play().then(() => {
        if (overlay) overlay.classList.add('is-hidden');
      }).catch(() => {
        // Autoplay might be blocked; keep overlay visible
      });
      return;
    }
    // External embed (YouTube/Vimeo)
    const iframe = slide.querySelector('iframe.product-video-embed');
    const embedOverlay = slide.querySelector('[data-embed-play]');
    if (iframe) {
      const base = iframe.dataset.origSrc || iframe.src || '';
      iframe.src = this.buildLoopingAutoplaySrc(base);
      if (embedOverlay) embedOverlay.classList.add('is-hidden');
      // Hide skeleton after a brief moment to avoid flash
      setTimeout(() => this.hideSkeleton(), 200);
    }
  }

  buildLoopingAutoplaySrc(urlStr) {
    try {
      const url = new URL(urlStr, window.location.origin);
      const host = url.hostname.toLowerCase();
      // common flags
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('muted', '1');
      url.searchParams.set('playsinline', '1');
      url.searchParams.set('loop', '1');
      if (host.includes('youtube.com') || host.includes('youtu.be')) {
        // Extract video id
        let id = '';
        if (host.includes('youtu.be')) {
          id = url.pathname.split('/').filter(Boolean).pop() || '';
        } else {
          const parts = url.pathname.split('/'); // /embed/ID
          const idx = parts.indexOf('embed');
          id = idx !== -1 ? parts[idx + 1] : '';
        }
        if (id) {
          url.searchParams.set('playlist', id);
        }
      }
      if (host.includes('vimeo.com')) {
        // Vimeo supports loop=1; ensure background not forced
        url.searchParams.set('background', '0');
      }
      return url.toString();
    } catch (_) {
      // Fallback: append minimal params
      const sep = urlStr.includes('?') ? '&' : '?';
      return urlStr + sep + 'autoplay=1&muted=1&loop=1&playsinline=1';
    }
  }

  updateTrack(animate) {
    if (!this.track) return;
    if (!animate) this.track.style.transition = 'none';
    else this.track.style.transition = '';
    const x = -this.currentIndex * 100;
    this.track.style.transform = `translate3d(${x}%, 0, 0)`;
    if (!animate) requestAnimationFrame(() => { this.track.style.transition = ''; });
  }

  updateThumbs() {
    this.thumbs.forEach((btn, i) => {
      btn.classList.toggle('active', i === this.currentIndex);
      btn.setAttribute('aria-selected', i === this.currentIndex ? 'true' : 'false');
    });
  }

  preloadAdjacent() {
    const ids = [this.currentIndex - 1, this.currentIndex + 1].map(i => ((i % this.slides.length) + this.slides.length) % this.slides.length);
    ids.forEach(i => {
      const img = this.slides[i]?.querySelector('img');
      if (img && !img.dataset._prefetched) {
        const src = img.currentSrc || img.src;
        if (src) {
          const pre = new Image();
          pre.src = src;
          img.dataset._prefetched = '1';
        }
      }
    });
  }

  onPointerDown(e) {
    this.gallery.setPointerCapture(e.pointerId);
    this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.activePointers.size === 2) {
      const [a, b] = Array.from(this.activePointers.values());
      this.isPinching = true;
      this.baseScale = this.scale || 1;
      this.startDist = Math.hypot(a.x - b.x, a.y - b.y);
    } else {
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragDeltaX = 0;
      this.track.style.transition = 'none';
    }
  }

  onPointerMove(e) {
    if (this.isPinching) {
      this.activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.activePointers.size < 2) return;
      const [a, b] = Array.from(this.activePointers.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const img = this.slides[this.currentIndex].querySelector('img');
      if (!img || !this.startDist) return;
      const scale = Math.min(2, Math.max(1, this.baseScale * (dist / this.startDist)));
      this.scale = scale;
      img.style.transform = `scale(${scale})`;
      img.style.transition = 'transform 80ms linear';
      return;
    }
    if (!this.isDragging) return;
    this.dragDeltaX = e.clientX - this.dragStartX;
    const percent = (this.dragDeltaX / this.gallery.clientWidth) * 100;
    const x = -this.currentIndex * 100 + percent;
    this.track.style.transform = `translate3d(${x}%, 0, 0)`;
  }

  onPointerUp(e) {
    this.activePointers.delete(e.pointerId);
    if (this.isPinching) {
      if (this.activePointers.size < 2) {
        this.isPinching = false;
        const img = this.slides[this.currentIndex].querySelector('img');
        if (img && (!this.scale || this.scale <= 1)) {
          this.scale = 1;
          img.style.transform = '';
        }
      }
      return;
    }
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.style.transition = '';
    const threshold = Math.min(80, this.gallery.clientWidth * 0.15);
    if (this.dragDeltaX > threshold) this.goTo(this.currentIndex - 1);
    else if (this.dragDeltaX < -threshold) this.goTo(this.currentIndex + 1);
    else this.updateTrack(true);
    this.dragDeltaX = 0;
  }

  showSkeleton() {
    if (this.skeleton) this.skeleton.classList.add('is-visible');
  }

  hideSkeleton() {
    if (this.skeleton) this.skeleton.classList.remove('is-visible');
  }

  placeholderDataURI() {
    const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="1600"><rect width="100%" height="100%" fill="%23f2f2f2"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-family="sans-serif" font-size="24">image unavailable</text></svg>';
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  initOptions() {
    if (!this.productData || !this.optionButtons.length) return;
    this.optionButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const name = btn.dataset.optionName;
        const value = btn.dataset.optionValue;
        if (!name || !value) return;
        // Toggle active within group
        this.root.querySelectorAll(`.product-page__option-btn[data-option-name="${CSS.escape(name)}"]`)
          .forEach(b => b.classList.toggle('active', b === btn));
        // Update selection
        this.selectedOptions[name] = value;
        // Find matching variant
        const variant = this.findVariantByOptions(this.selectedOptions);
        if (variant) {
          const idInput = this.form?.querySelector('input[name="id"]');
          if (idInput) idInput.value = variant.id;
          this.updatePrice(variant);
          const submit = this.form?.querySelector('[type="submit"]');
          if (submit) submit.disabled = !variant.available;
        }
      });
    });
  }

  findVariantByOptions(selected) {
    if (!this.productData?.variants) return null;
    return this.productData.variants.find(v => {
      if (!v.options || v.options.length === 0) return false;
      // Map product option names to chosen values
      const names = this.productData.options || [];
      return v.options.every((optValue, idx) => {
        const optName = names[idx];
        return selected[optName] === optValue;
      });
    });
  }

  updatePrice(variant) {
    if (!this.priceEl || !variant) return;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    });
    const price = formatter.format(variant.price / 100);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const compare = formatter.format(variant.compare_at_price / 100);
      this.priceEl.innerHTML = `<span>${price}</span> <span class="product-card__compare-price">${compare}</span>`;
    } else {
      this.priceEl.textContent = price;
    }
  }

  initQuantity() {
    if (!this.quantityInput) return;
    if (this.qtyMinus) {
      this.qtyMinus.addEventListener('click', () => {
        const val = Math.max(1, parseInt(this.quantityInput.value || '1', 10) - 1);
        this.quantityInput.value = String(val);
      });
    }
    if (this.qtyPlus) {
      this.qtyPlus.addEventListener('click', () => {
        const val = Math.max(1, parseInt(this.quantityInput.value || '1', 10) + 1);
        this.quantityInput.value = String(val);
      });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductPage();
});
